import type {
  ChatMode,
  Chunk,
  MessageInfoChunk,
  SessionConversation,
  SummaryItem,
  TimelineEntry,
} from '../../contracts/stream/types';
import {
  createAppointmentsEntry,
  createCalculatorEntry,
  createCapabilityToolEntry,
  createClinicalTaskResultEntry,
  createCreateTemplateEntry,
  createCsvExportEntry,
  createEditTemplateSectionEntry,
  createEmailPatientEvidenceEntry,
  createExcelExportEntry,
  createFormFillEntry,
  createFormulaCalculatorEntry,
  createGetTemplatePrefsEntry,
  createInteractiveWidgetEntry,
  createLibrarySearchEntry,
  createPatientEvidencePdfEntry,
  createPatientSummaryEntry,
  createPdfExportEntry,
  createPptxExportEntry,
  createPrintDocumentEntry,
  createQuotaExhaustedEntry,
  createReasoningEntry,
  createSearchCommunityTemplatesEntry,
  createSearchEntry,
  createSearchMyTemplatesEntry,
  createSearchPatientProfilesEntry,
  createSearchTemplatesEntry,
  createSessionsByDateEntry,
  createTextEntry,
  createThinkingEntry,
  createTierRestrictedToolErrorEntry,
  createUnauthenticatedToolAccessErrorEntry,
  createUnsupportedToolEntry,
  createVisualDxEntry,
  createWordExportEntry,
} from './chatHelpers';

export interface ProcessChunkResult {
  timeline: TimelineEntry[];
  streamingState: SessionConversation['streamingState'];
}

/** Shared fields common to all export-type chunk updates (Excel, PPTX, etc.).
 *  `status` is intentionally excluded to preserve the narrowed union type.
 *
 *  `downloadUrl` is a durable backend URL (`/api/v2/ml-scribe/chat/exports/
 *  download?key=...`) that 302-redirects to a freshly-minted S3 presigned
 *  URL on every click, so we can persist it on the chat entry indefinitely
 *  without worrying about presigned-URL expiry. */
const exportChunkBaseFields = (
  chunk: { download_url?: string; filename?: string; error?: string },
  existing: { downloadUrl?: string; filename: string; error?: string }
) => ({
  downloadUrl: chunk.download_url ?? existing.downloadUrl,
  filename: chunk.filename ?? existing.filename,
  error: chunk.error ?? existing.error,
});

export const processChatChunk = (
  chunk: Chunk,
  session: SessionConversation,
  streamingMode?: ChatMode
): ProcessChunkResult | null => {
  const { streamingState } = session;

  if (!streamingState) {
    return null;
  }

  // Handle message_info chunks: capture message_id for feedback tracking.
  // This chunk has index: -1 and is emitted before any content chunks.
  // We intercept it before the index guard to avoid creating timeline entries.
  // Guard is loosened to always catch message_info regardless of payload shape,
  // so it never falls through to the unsupported tool fallback.
  if (chunk.type === 'message_info') {
    const messageId =
      'message_id' in chunk ? (chunk as MessageInfoChunk).message_id : undefined;
    return {
      timeline: session.timeline,
      streamingState: {
        ...streamingState,
        lastChunkAt: Date.now(),
        ...(messageId ? { messageId } : {}),
      },
    };
  }

  // EVI-1567 / EV-023: drug-reference signal. Index-less, emitted once the
  // answer has finished streaming (deferred from the flag_drug_reference tool
  // call). Mark the current streaming response so the drug-interaction warning
  // badge can render at the end of the answer — both for text blocks that
  // already exist and any that arrive afterwards (via streamingState below).
  if (chunk.type === 'drug_referenced') {
    const basePosition = streamingState.basePosition;
    const timeline = session.timeline.map((entry) =>
      entry.type === 'text_item' && entry.position >= basePosition
        ? { ...entry, drugReferenced: true }
        : entry
    );
    return {
      timeline,
      streamingState: {
        ...streamingState,
        lastChunkAt: Date.now(),
        drugReferenced: true,
      },
    };
  }

  if (!('index' in chunk)) {
    return null;
  }

  const backendIndex = chunk.index;
  const timelinePosition = streamingState.basePosition + backendIndex;
  const existingPosition = streamingState.activeIndices.get(backendIndex);

  const now = Date.now();
  // Indexed chunks (text, search, thinking, reasoning, tools) reset the
  // progressive timeout. Thinking/reasoning render a ThinkingBlock — they are
  // not silence (EVI-2477). Keepalives are not chunks; they only bump
  // lastChunkAt via onKeepalive.
  let timeline = [...session.timeline];
  let updatedStreamingState = {
    ...streamingState,
    lastChunkAt: now,
    lastVisibleContentAt: now,
    streamSlow: false,
  };

  const placeholderIndex = timeline.findIndex(
    (entry) => entry.type === 'reasoning_item' && entry.backendIndex === -1
  );
  if (placeholderIndex !== -1) {
    timeline = timeline.filter((_, idx) => idx !== placeholderIndex);
  }

  // Force-complete any thinking_item entries that are still active when a new
  // higher-index entry arrives. The backend emits a `status: "complete"` chunk
  // for each thinking block, but if it's dropped or arrives out of order the
  // ThinkingBlock UI gets stuck in the loading state. This invariant ensures:
  // "if a chunk at index N is being processed, every thinking entry at index < N
  // must already be complete."
  if (existingPosition === undefined) {
    for (let i = 0; i < timeline.length; i++) {
      const entry = timeline[i];
      if (
        entry?.type === 'thinking_item' &&
        entry.status !== 'complete' &&
        entry.backendIndex < backendIndex
      ) {
        timeline[i] = { ...entry, status: 'complete' };
      }
    }
  }

  // FIX EVI-477: Use === undefined instead of falsy check because existingPosition
  // can legitimately be 0 (first position in timeline). Using !existingPosition would
  // incorrectly create duplicate entries when updating the entry at position 0.
  if (existingPosition === undefined) {
    let newEntry: TimelineEntry;

    if (chunk.type === 'reasoning') {
      newEntry = createReasoningEntry(chunk, timelinePosition, streamingMode);
    } else if (chunk.type === 'web_search_call') {
      // EVI-1486: pass backend message_id so search-block click events
      // join to the same message row as text-surface clicks. The
      // `message_info` chunk that delivers this id arrives at index -1,
      // before any `web_search_call`, so it's reliably populated here.
      newEntry = createSearchEntry(
        chunk,
        timelinePosition,
        streamingMode,
        streamingState.messageId
      );
    } else if (chunk.type === 'library_search_call') {
      newEntry = createLibrarySearchEntry(
        chunk,
        timelinePosition,
        streamingMode,
        streamingState.messageId
      );
    } else if (chunk.type === 'patient_summary') {
      // EVI-591: Handle patient summary (pre-chart) tool calls
      newEntry = createPatientSummaryEntry(chunk, timelinePosition, streamingMode);
    } else if (chunk.type === 'capability_tool') {
      // Handle protocol capability tool calls (e.g., get_patient_summary, execute_template_workflow)
      newEntry = createCapabilityToolEntry(chunk, timelinePosition, streamingMode);
    } else if (chunk.type === 'text') {
      const textEntry = createTextEntry(
        chunk,
        timelinePosition,
        streamingMode,
        streamingState.messageId
      );
      // EVI-1567: a <drug-referenced/> signal earlier this response flags every
      // text block in it, including ones created after the signal arrived.
      newEntry = streamingState.drugReferenced
        ? { ...textEntry, drugReferenced: true }
        : textEntry;
    } else if (chunk.type === 'thinking') {
      // EVI-334: Handle thinking chunks
      newEntry = createThinkingEntry(chunk, timelinePosition, streamingMode);
    } else if (chunk.type === 'pdf_export') {
      newEntry = createPdfExportEntry(chunk, timelinePosition, streamingMode);
    } else if (chunk.type === 'print_document') {
      newEntry = createPrintDocumentEntry(chunk, timelinePosition, streamingMode);
    } else if (chunk.type === 'patient_evidence_pdf') {
      newEntry = createPatientEvidencePdfEntry(chunk, timelinePosition, streamingMode);
    } else if (chunk.type === 'email_patient_evidence') {
      newEntry = createEmailPatientEvidenceEntry(chunk, timelinePosition, streamingMode);
    } else if (chunk.type === 'form_fill') {
      newEntry = createFormFillEntry(chunk, timelinePosition, streamingMode);
    } else if (chunk.type === 'medical_calculator') {
      // EVI-544: Handle medical calculator tool calls
      newEntry = createCalculatorEntry(chunk, timelinePosition, streamingMode);
    } else if (chunk.type === 'visualdx') {
      newEntry = createVisualDxEntry(chunk, timelinePosition, streamingMode);
    } else if (chunk.type === 'interactive_widget') {
      newEntry = createInteractiveWidgetEntry(chunk, timelinePosition, streamingMode);
    } else if (chunk.type === 'calculator_result') {
      // EVI-620: Handle formula calculator tool calls (dynamic calculator)
      newEntry = createFormulaCalculatorEntry(chunk, timelinePosition, streamingMode);
    } else if (chunk.type === 'clinical_task_result') {
      // EVI-490: Handle clinical task result chunks
      newEntry = createClinicalTaskResultEntry(chunk, timelinePosition, streamingMode);
    } else if (chunk.type === 'sessions_by_date') {
      // Handle sessions by date chunks (Patients in a Day feature)
      newEntry = createSessionsByDateEntry(chunk, timelinePosition, streamingMode);
    } else if (chunk.type === 'csv_export') {
      // Handle CSV export chunks
      newEntry = createCsvExportEntry(chunk, timelinePosition, streamingMode);
    } else if (chunk.type === 'word_export') {
      // Handle Word document export chunks
      newEntry = createWordExportEntry(chunk, timelinePosition, streamingMode);
    } else if (chunk.type === 'excel_export') {
      // Handle Excel spreadsheet export chunks
      newEntry = createExcelExportEntry(chunk, timelinePosition, streamingMode);
    } else if (chunk.type === 'pptx_export') {
      // Handle PowerPoint presentation export chunks
      newEntry = createPptxExportEntry(chunk, timelinePosition, streamingMode);
    } else if (chunk.type === 'appointments') {
      // Handle appointment retrieval chunks
      newEntry = createAppointmentsEntry(chunk, timelinePosition, streamingMode);
    } else if (chunk.type === 'search_templates') {
      // Handle template search chunks
      newEntry = createSearchTemplatesEntry(chunk, timelinePosition, streamingMode);
    } else if (chunk.type === 'search_community_templates') {
      // Handle community template search chunks
      newEntry = createSearchCommunityTemplatesEntry(
        chunk,
        timelinePosition,
        streamingMode
      );
    } else if (chunk.type === 'search_my_templates') {
      // Handle user's own template search chunks
      newEntry = createSearchMyTemplatesEntry(chunk, timelinePosition, streamingMode);
    } else if (chunk.type === 'get_template_and_preferences') {
      // Handle template & preferences fetch chunks
      newEntry = createGetTemplatePrefsEntry(chunk, timelinePosition, streamingMode);
    } else if (chunk.type === 'create_template') {
      // Handle Ask Heidi creating a new personal template
      newEntry = createCreateTemplateEntry(chunk, timelinePosition, streamingMode);
    } else if (chunk.type === 'edit_template_section') {
      // Handle Ask Heidi editing a template section
      newEntry = createEditTemplateSectionEntry(chunk, timelinePosition, streamingMode);
    } else if (chunk.type === 'search_patient_profiles') {
      // Handle patient profile search chunks
      newEntry = createSearchPatientProfilesEntry(chunk, timelinePosition, streamingMode);
    } else if (chunk.type === 'unauthenticated_tool_access_error') {
      // Unauthenticated user tried to use a tool that requires auth
      newEntry = createUnauthenticatedToolAccessErrorEntry(
        chunk,
        timelinePosition,
        streamingMode
      );
    } else if (chunk.type === 'tier_restricted_tool_error') {
      newEntry = createTierRestrictedToolErrorEntry(
        chunk,
        timelinePosition,
        streamingMode
      );
    } else if (chunk.type === 'quota_exhausted') {
      newEntry = createQuotaExhaustedEntry(chunk, timelinePosition, streamingMode);
    } else {
      // Guard: skip known metadata/non-renderable chunk types that should
      // never create timeline entries. Prevents false UnsupportedToolBlock
      // rendering when backend sends metadata with an index field.
      const NON_RENDERABLE_TYPES = new Set([
        'message_info',
        'search_metadata',
        'chat_metadata',
        'mode_info',
        'in_session_evidence_quota_update',
        'error',
      ]);
      if (NON_RENDERABLE_TYPES.has((chunk as { type: string }).type)) {
        return null;
      }

      // Fallback: unknown/unsupported tool type (desktop release desync).
      // Instead of silently dropping the chunk, render a generic fallback block
      // so the user sees that the agent performed an action even when the frontend
      // doesn't have a dedicated component for it yet.
      newEntry = createUnsupportedToolEntry(
        chunk as import('../../contracts/stream/types').UnsupportedToolChunk,
        timelinePosition,
        streamingMode
      );
    }

    // Safely add entry to timeline, preventing sparse arrays
    // When switching between chats during concurrent streaming, timelinePosition
    // might exceed array length. Use push instead of direct assignment to prevent gaps.
    if (timelinePosition >= timeline.length) {
      // Position is at or beyond end - append to maintain continuity
      timeline.push(newEntry);
      // Use actual position in timeline for tracking
      const actualPosition = timeline.length - 1;

      const newActiveIndices = new Map(updatedStreamingState.activeIndices);
      newActiveIndices.set(backendIndex, actualPosition);

      updatedStreamingState = {
        ...updatedStreamingState,
        activeIndices: newActiveIndices,
        currentActiveIndex: backendIndex,
      };
    } else {
      // Position exists in array - safe to assign
      timeline[timelinePosition] = newEntry;

      const newActiveIndices = new Map(updatedStreamingState.activeIndices);
      newActiveIndices.set(backendIndex, timelinePosition);

      updatedStreamingState = {
        ...updatedStreamingState,
        activeIndices: newActiveIndices,
        currentActiveIndex: backendIndex,
      };
    }

    if (chunk.type === 'text') {
      updatedStreamingState = {
        ...updatedStreamingState,
        activityGroupOpen: false,
      };
    }
  } else {
    const existingEntry = timeline[existingPosition];

    if (existingEntry?.type === 'reasoning_item' && chunk.type === 'reasoning') {
      const reasoningContent =
        chunk.summary?.map((s: SummaryItem) => [s.text, s.index] as const) || [];
      timeline[existingPosition] = {
        ...existingEntry,
        content: [...existingEntry.content, ...reasoningContent],
      };
    } else if (existingEntry?.type === 'text_item' && chunk.type === 'text') {
      timeline[existingPosition] = {
        ...existingEntry,
        content: existingEntry.content + (chunk.text || ''),
        annotations: [...existingEntry.annotations, ...(chunk.annotations || [])],
        // EVI-1567: keep the flag once set; also pick it up if the signal
        // arrived before this block existed.
        drugReferenced: existingEntry.drugReferenced || streamingState.drugReferenced,
      };
    } else if (
      existingEntry?.type === 'web_search_item' &&
      chunk.type === 'web_search_call'
    ) {
      // Update web search entry when "completed" chunk arrives with sources and images
      // Flow: "searching" (sources: []) → "completed" (sources: [...], images: [...])
      timeline[existingPosition] = {
        ...existingEntry,
        query: chunk.action?.query || existingEntry.query,
        sources: chunk.action?.sources || existingEntry.sources,
        images: chunk.action?.images || existingEntry.images,
        status: chunk.status,
      };
    } else if (
      existingEntry?.type === 'library_search_item' &&
      chunk.type === 'library_search_call'
    ) {
      // Update library search entry when "completed" chunk arrives with sources
      // Flow: "searching" (sources: []) → "completed" (sources: [...])
      timeline[existingPosition] = {
        ...existingEntry,
        query: chunk.action?.query || existingEntry.query,
        sources: chunk.action?.sources || existingEntry.sources,
        status: chunk.status,
      };
    } else if (
      existingEntry?.type === 'patient_summary_item' &&
      chunk.type === 'patient_summary'
    ) {
      // EVI-591: Update patient summary entry when status changes
      // Flow: "fetching" → "completed" or "error"
      timeline[existingPosition] = {
        ...existingEntry,
        status: chunk.status,
        error: chunk.error ?? existingEntry.error,
      };
    } else if (
      existingEntry?.type === 'capability_tool_item' &&
      chunk.type === 'capability_tool'
    ) {
      // Update capability tool entry when status changes
      // Flow: "executing" → "completed" or "error"
      timeline[existingPosition] = {
        ...existingEntry,
        status: chunk.status,
        error: chunk.error ?? existingEntry.error,
      };
    } else if (existingEntry?.type === 'thinking_item' && chunk.type === 'thinking') {
      // EVI-334: Update thinking entry with streamed content or completion
      // Accumulate thinking content for streaming, update status/duration on complete
      timeline[existingPosition] = {
        ...existingEntry,
        status: chunk.status,
        thinking:
          chunk.status === 'streaming'
            ? (existingEntry.thinking || '') + (chunk.thinking || '')
            : chunk.thinking || existingEntry.thinking,
        durationMs: chunk.duration_ms ?? existingEntry.durationMs,
      };
    } else if (existingEntry?.type === 'pdf_export_item' && chunk.type === 'pdf_export') {
      // Update PDF export entry when "completed" chunk arrives with download URL
      // This handles the flow: "generating" → "completed" (with download_url and filename)
      timeline[existingPosition] = {
        ...existingEntry,
        status: chunk.status,
        downloadUrl: chunk.download_url ?? existingEntry.downloadUrl,
        filename: chunk.filename ?? existingEntry.filename,
      };
    } else if (
      existingEntry?.type === 'print_document_item' &&
      chunk.type === 'print_document'
    ) {
      // Flow: "generating" → "completed" (browser-print side effect
      // is triggered in useChatStream, not here, so this reducer stays pure).
      timeline[existingPosition] = {
        ...existingEntry,
        status: chunk.status,
        downloadUrl: chunk.download_url ?? existingEntry.downloadUrl,
        filename: chunk.filename ?? existingEntry.filename,
        error: chunk.error ?? existingEntry.error,
      };
    } else if (
      existingEntry?.type === 'patient_evidence_pdf_item' &&
      chunk.type === 'patient_evidence_pdf'
    ) {
      // Update patient evidence PDF entry when status changes
      // Flow: "generating" → "completed" (with download_url) or "error"
      timeline[existingPosition] = {
        ...existingEntry,
        status: chunk.status,
        downloadUrl: chunk.download_url ?? existingEntry.downloadUrl,
        filename: chunk.filename ?? existingEntry.filename,
        conditionsIncluded: chunk.conditions_included ?? existingEntry.conditionsIncluded,
        error: chunk.error ?? existingEntry.error,
      };
    } else if (existingEntry?.type === 'form_fill_item' && chunk.type === 'form_fill') {
      timeline[existingPosition] = {
        ...existingEntry,
        status: chunk.status,
        downloadUrl: chunk.download_url ?? existingEntry.downloadUrl,
        filename: chunk.filename ?? existingEntry.filename,
        fieldsFilled: chunk.fields_filled ?? existingEntry.fieldsFilled,
        error: chunk.error ?? existingEntry.error,
      };
    } else if (
      existingEntry?.type === 'email_patient_evidence_item' &&
      chunk.type === 'email_patient_evidence'
    ) {
      // Update email patient evidence entry when status changes
      // Flow: "sending" → "completed" or "error"
      timeline[existingPosition] = {
        ...existingEntry,
        status: chunk.status,
        message: chunk.message ?? existingEntry.message,
        error: chunk.error ?? existingEntry.error,
        needsEmail: chunk.needs_email ?? existingEntry.needsEmail,
        needsPdf: chunk.needs_pdf ?? existingEntry.needsPdf,
      };
    } else if (
      existingEntry?.type === 'calculator_item' &&
      chunk.type === 'medical_calculator'
    ) {
      // EVI-544: Update calculator entry when status changes
      // Flow: "calculating" → "completed" (with result) or "error"
      timeline[existingPosition] = {
        ...existingEntry,
        status: chunk.status,
        result: chunk.result ?? existingEntry.result,
        error: chunk.error ?? existingEntry.error,
      };
    } else if (existingEntry?.type === 'visualdx_item' && chunk.type === 'visualdx') {
      timeline[existingPosition] = {
        ...existingEntry,
        status: chunk.status,
        error: chunk.error ?? existingEntry.error,
        // Preserve structured data from the completed chunk
        diagnoses: chunk.diagnoses ?? existingEntry.diagnoses,
        compare_url:
          chunk.compare_url !== undefined ? chunk.compare_url : existingEntry.compare_url,
        detail: chunk.detail ?? existingEntry.detail,
      };
    } else if (
      existingEntry?.type === 'interactive_widget_item' &&
      chunk.type === 'interactive_widget'
    ) {
      // Flow: "fetching" → "completed" or "error". For the diagnostic
      // flow widget the BE only needs to ack the lifecycle (the widget
      // is purely client-side); for the ranked-differential widget the
      // completed chunk also carries the merged differential payload.
      timeline[existingPosition] = {
        ...existingEntry,
        status: chunk.status,
        error: chunk.error ?? existingEntry.error,
        differential: chunk.differential ?? existingEntry.differential,
        gallery: chunk.gallery ?? existingEntry.gallery,
        compare: chunk.compare ?? existingEntry.compare,
        drugReaction: chunk.drugReaction ?? existingEntry.drugReaction,
        findings: chunk.findings ?? existingEntry.findings,
        builtDifferential: chunk.builtDifferential ?? existingEntry.builtDifferential,
        handout: chunk.handout ?? existingEntry.handout,
      };
    } else if (
      existingEntry?.type === 'formula_calculator_item' &&
      chunk.type === 'calculator_result'
    ) {
      // EVI-620: Update formula calculator entry when status changes
      // Flow: "calculating" → "completed" (with result) or "error"
      timeline[existingPosition] = {
        ...existingEntry,
        status: chunk.status,
        formulaExpression: chunk.formula_expression ?? existingEntry.formulaExpression,
        formulaDisplay: chunk.formula_display ?? existingEntry.formulaDisplay,
        variables: chunk.variables ?? existingEntry.variables,
        result: chunk.result ?? existingEntry.result,
        sourceUrl: chunk.source_url ?? existingEntry.sourceUrl,
        sourceName: chunk.source_name ?? existingEntry.sourceName,
        validationReference:
          chunk.validation_reference ?? existingEntry.validationReference,
        criteriaBreakdown: chunk.criteria_breakdown ?? existingEntry.criteriaBreakdown,
        calculationSteps: chunk.calculation_steps ?? existingEntry.calculationSteps,
        warnings: chunk.warnings ?? existingEntry.warnings,
        error: chunk.error ?? existingEntry.error,
      };
    } else if (
      existingEntry?.type === 'clinical_task_result_item' &&
      chunk.type === 'clinical_task_result'
    ) {
      // EVI-490: Update clinical task result entry when status changes
      // Flow: "creating" → "completed" (with tasks) or "failed"
      timeline[existingPosition] = {
        ...existingEntry,
        status: chunk.status,
        tasks: chunk.tasks ?? existingEntry.tasks,
        error: chunk.error ?? existingEntry.error,
      };
    } else if (
      existingEntry?.type === 'sessions_by_date_item' &&
      chunk.type === 'sessions_by_date'
    ) {
      // Update sessions by date entry when status changes
      // Flow: "fetching" → "completed" (with sessions) or "error"
      timeline[existingPosition] = {
        ...existingEntry,
        status: chunk.status,
        date: chunk.date ?? existingEntry.date,
        totalSessions: chunk.total_sessions ?? existingEntry.totalSessions,
        hasMore: chunk.has_more ?? existingEntry.hasMore,
        sessions: chunk.sessions ?? existingEntry.sessions,
        error: chunk.error ?? existingEntry.error,
      };
    } else if (
      existingEntry?.type === 'appointments_item' &&
      chunk.type === 'appointments'
    ) {
      // Update appointments entry when status changes
      // Flow: "fetching" → "completed" (with appointments) or "error"
      timeline[existingPosition] = {
        ...existingEntry,
        status: chunk.status,
        date: chunk.date ?? existingEntry.date,
        totalAppointments: chunk.total_appointments ?? existingEntry.totalAppointments,
        appointments: chunk.appointments ?? existingEntry.appointments,
        error: chunk.error ?? existingEntry.error,
      };
    } else if (
      existingEntry?.type === 'search_templates_item' &&
      chunk.type === 'search_templates'
    ) {
      // Update search templates entry when status changes
      // Flow: "searching" → "completed" or "error"
      timeline[existingPosition] = {
        ...existingEntry,
        status: chunk.status,
        totalResults: chunk.total_results ?? existingEntry.totalResults,
        error: chunk.error ?? existingEntry.error,
      };
    } else if (
      existingEntry?.type === 'search_community_templates_item' &&
      chunk.type === 'search_community_templates'
    ) {
      // Update search community templates entry when status changes
      // Flow: "searching" → "completed" or "error"
      timeline[existingPosition] = {
        ...existingEntry,
        status: chunk.status,
        totalResults: chunk.total_results ?? existingEntry.totalResults,
        error: chunk.error ?? existingEntry.error,
      };
    } else if (
      existingEntry?.type === 'search_my_templates_item' &&
      chunk.type === 'search_my_templates'
    ) {
      timeline[existingPosition] = {
        ...existingEntry,
        status: chunk.status,
        totalResults: chunk.total_results ?? existingEntry.totalResults,
        error: chunk.error ?? existingEntry.error,
      };
    } else if (
      existingEntry?.type === 'get_template_prefs_item' &&
      chunk.type === 'get_template_and_preferences'
    ) {
      timeline[existingPosition] = {
        ...existingEntry,
        status: chunk.status,
        error: chunk.error ?? existingEntry.error,
        templateAvailable: chunk.template_available ?? existingEntry.templateAvailable,
        templateName: chunk.template_name ?? existingEntry.templateName,
      };
    } else if (
      existingEntry?.type === 'create_template_item' &&
      chunk.type === 'create_template'
    ) {
      // Update create_template entry: applying -> completed (with template_id) or error
      timeline[existingPosition] = {
        ...existingEntry,
        status: chunk.status,
        templateId: chunk.template_id ?? existingEntry.templateId,
        templateName: chunk.template_name ?? existingEntry.templateName,
        reason: chunk.reason ?? existingEntry.reason,
        error:
          chunk.status === 'completed' ? undefined : (chunk.error ?? existingEntry.error),
      };
    } else if (
      existingEntry?.type === 'edit_template_section_item' &&
      chunk.type === 'edit_template_section'
    ) {
      // Update edit_template_section entry: applying -> completed (with template_id) or error
      timeline[existingPosition] = {
        ...existingEntry,
        status: chunk.status,
        templateId: chunk.template_id ?? existingEntry.templateId,
        templateName: chunk.template_name ?? existingEntry.templateName,
        editsApplied: chunk.edits_applied ?? existingEntry.editsApplied,
        reason: chunk.reason ?? existingEntry.reason,
        error:
          chunk.status === 'completed' ? undefined : (chunk.error ?? existingEntry.error),
      };
    } else if (
      existingEntry?.type === 'search_patient_profiles_item' &&
      chunk.type === 'search_patient_profiles'
    ) {
      // Update search patient profiles entry when status changes
      // Flow: "searching" → "completed" or "error"
      timeline[existingPosition] = {
        ...existingEntry,
        status: chunk.status,
        totalResults: chunk.total_results ?? existingEntry.totalResults,
        error: chunk.error ?? existingEntry.error,
      };
    } else if (existingEntry?.type === 'csv_export_item' && chunk.type === 'csv_export') {
      // Update CSV export entry when status changes
      // Flow: "generating" → "completed" (with download_url) or "error"
      timeline[existingPosition] = {
        ...existingEntry,
        status: chunk.status,
        downloadUrl: chunk.download_url ?? existingEntry.downloadUrl,
        filename: chunk.filename ?? existingEntry.filename,
        rowCount: chunk.row_count ?? existingEntry.rowCount,
        error: chunk.error ?? existingEntry.error,
      };
    } else if (
      existingEntry?.type === 'word_export_item' &&
      chunk.type === 'word_export'
    ) {
      // Update Word export entry when status changes
      // Flow: "generating" → "completed" (with download_url) or "error"
      timeline[existingPosition] = {
        ...existingEntry,
        status: chunk.status,
        downloadUrl: chunk.download_url ?? existingEntry.downloadUrl,
        filename: chunk.filename ?? existingEntry.filename,
        sessionCount: chunk.session_count ?? existingEntry.sessionCount,
        error: chunk.error ?? existingEntry.error,
      };
    } else if (
      existingEntry?.type === 'excel_export_item' &&
      chunk.type === 'excel_export'
    ) {
      timeline[existingPosition] = {
        ...existingEntry,
        status: chunk.status,
        ...exportChunkBaseFields(chunk, existingEntry),
        rowCount: chunk.row_count ?? existingEntry.rowCount,
      };
    } else if (
      existingEntry?.type === 'pptx_export_item' &&
      chunk.type === 'pptx_export'
    ) {
      timeline[existingPosition] = {
        ...existingEntry,
        status: chunk.status,
        ...exportChunkBaseFields(chunk, existingEntry),
        previewUrl: chunk.preview_url ?? existingEntry.previewUrl,
        slideCount: chunk.slide_count ?? existingEntry.slideCount,
        presentationId: chunk.presentation_id ?? existingEntry.presentationId,
      };
    } else if (existingEntry?.type === 'unsupported_tool_item') {
      const unsupportedChunk = chunk as {
        status?: string;
        result_data?: Record<string, unknown>;
        error?: string;
      };
      timeline[existingPosition] = {
        ...existingEntry,
        status:
          typeof unsupportedChunk.status === 'string'
            ? unsupportedChunk.status
            : existingEntry.status,
        resultData:
          unsupportedChunk.result_data && typeof unsupportedChunk.result_data === 'object'
            ? unsupportedChunk.result_data
            : existingEntry.resultData,
        errorMessage:
          typeof unsupportedChunk.error === 'string'
            ? unsupportedChunk.error
            : existingEntry.errorMessage,
      };
    }

    updatedStreamingState = {
      ...updatedStreamingState,
      currentActiveIndex: backendIndex,
    };
  }

  return {
    timeline,
    streamingState: updatedStreamingState,
  };
};
